import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useParams, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import { applicationsApi } from '../api/admin';
import { employerVerificationDashboardApi } from '../api/employerVerificationDashboardApi';
import {
  APPLICATION_NOT_FOUND_ADMIN_BODY,
  APPLICATION_NOT_FOUND_ADMIN_TITLE,
  isApplicationNotFoundAxiosError,
  messageFromOrchestratorAxiosError,
} from '../lib/axiosErrorMessage';
import { loanStatusPlain } from '../lib/applicationJourneyCopy';
import { isEmployerFootprintActive } from '../lib/employerFootprintCopy';
import { applicationsListPathFromNavState } from '../lib/applicationsListFilters';
import {
  ApplicationDecisionTab,
  ApplicationEmployerTab,
  ApplicationIdentityTab,
  ApplicationIncomeTab,
} from './applicationDetailVerificationPanels';
import { ApplicationDetailTabBar } from '../components/application/ApplicationDetailTabBar';
import { ApplicationDeviceIntelligenceTab } from '../components/application/ApplicationDeviceIntelligenceTab';
import { ApplicationLoanReviewTab } from '../components/loanReview/ApplicationLoanReviewTab';
import type { IncomeSubTarget } from '../lib/loanReview/mapLoanReviewToViewModel';
import { ApplicationOverviewTab } from '../components/application/ApplicationOverviewTab';
import { ApplicationDataAndNotesTab } from '../components/application/ApplicationDataAndNotesTab';
import { ApplicationEditingProvider } from '../components/application/EditableDetailRow';
import { ApplicationEmployeeDecisionPanel } from '../components/verification/ApplicationEmployeeDecisionPanel';
import {
  TAB_INDEX_FROM_QUERY,
  TAB_QUERY_BY_INDEX,
  computeApplicationTabStatuses,
  firstEnabledTabIndex,
  isApplicationTabEnabled,
  type ApplicationTabKey,
} from '../lib/applicationDetailTabStatus';
import { useAdminPageChrome, type AdminPageChromeState } from '../context/AdminPageChromeContext';

function loanStatusChipColor(status: string): 'default' | 'success' | 'warning' | 'error' {
  const key = status.replace(/_/g, ' ').trim().toLowerCase();
  if (key === 'active' || key === 'approved' || key === 'funded') return 'success';
  if (key === 'declined' || key === 'withdrawn') return 'error';
  if (key === 'pending' || key === 'review' || key === 'manual review') return 'warning';
  return 'default';
}

export function ApplicationDetailPage() {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const location = useLocation();
  const { applicationId = '' } = useParams<{ applicationId: string }>();
  const [search, setSearchParams] = useSearchParams();
  const tabQuery = (search.get('tab') ?? '').toLowerCase();
  const decodedId = useMemo(() => decodeURIComponent(applicationId), [applicationId]);
  const applicationsListTo = useMemo(
    () => applicationsListPathFromNavState(location.state),
    [location.state],
  );
  const [tab, setTab] = useState(
    () => TAB_INDEX_FROM_QUERY[tabQuery] ?? 0,
  );
  /**
   * A hero bullet (e.g. "gambling transactions ... see Income tab") can ask for
   * a specific Income sub-view, not just the tab. Bumping the nonce on every
   * jump lets re-clicking the same bullet re-open the target even when its
   * value has not changed - the same reason jumpToCrossCheck's jumpNonce exists.
   */
  const [incomeJump, setIncomeJump] = useState<{ target: IncomeSubTarget; nonce: number } | null>(null);
  // Kept outside the state so clearing the jump does not reset the count: a fresh
  // deep link always gets a new nonce, even right after a plain tab switch.
  const incomeJumpNonce = useRef(0);

  /** Switch tab in UI + URL together (loan-review deep links and tab bar). */
  const selectTab = useCallback(
    (index: number, opts?: { replace?: boolean }) => {
      const key = TAB_QUERY_BY_INDEX[index];
      setTab(index);
      // A plain tab switch consumes any pending deep link. Tabs unmount when left,
      // so a jump still held here re-fired on every return to the Income tab and
      // forced the Gambling view the reviewer had already navigated away from.
      setIncomeJump(null);
      if (!key) return;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', key);
          return next;
        },
        { replace: opts?.replace ?? false },
      );
    },
    [setSearchParams],
  );

  const selectTabByKey = useCallback(
    (key: ApplicationTabKey, incomeSubTarget?: IncomeSubTarget | null) => {
      const index = TAB_INDEX_FROM_QUERY[key];
      if (index === undefined) return;
      selectTab(index);
      if (key === 'income' && incomeSubTarget) {
        incomeJumpNonce.current += 1;
        setIncomeJump({ target: incomeSubTarget, nonce: incomeJumpNonce.current });
      }
      requestAnimationFrame(() => {
        document
          .getElementById('application-detail-tab-panel')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },
    [selectTab],
  );

  const journeyQ = useQuery({
    queryKey: ['applicationJourney', decodedId],
    queryFn: () => applicationsApi.journey(decodedId),
    enabled: !!decodedId,
    retry: false,
    // Status can change out-of-band (e.g. customer withdrawal while an admin
    // reviewer has the file open) — always prefer a fresh journey read.
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: (q) => {
      const status = String(q.state.data?.application?.status ?? '').trim().toUpperCase();
      // Customer withdrawal can land while a reviewer keeps this file open.
      return status === 'MANUAL_REVIEW' ? 15_000 : false;
    },
  });

  const deviceRiskQ = useQuery({
    queryKey: ['applicationDeviceRisk', decodedId],
    queryFn: () => applicationsApi.deviceRisk(decodedId),
    enabled: !!decodedId,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const footprintQ = useQuery({
    queryKey: ['employerFootprintAdmin', decodedId],
    queryFn: () => employerVerificationDashboardApi.employerFootprint(decodedId),
    enabled: !!decodedId,
    staleTime: 30_000,
    refetchInterval: (q) => {
      const s = String(q.state.data?.status ?? '').toUpperCase();
      return s === 'PENDING' || s === 'RUNNING' ? 5_000 : false;
    },
  });

  const journeyApplication = journeyQ.data?.application;
  const statusChipColor = journeyApplication ? loanStatusChipColor(journeyApplication.status) : 'default';

  const chromeStart = useMemo(
    () => (
      <Tooltip title="Back to applications">
        <IconButton
          component={RouterLink}
          to={applicationsListTo}
          size="small"
          aria-label="Back to applications"
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}
        >
          <ArrowBackRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ),
    [applicationsListTo],
  );

  const chromeEnd = useMemo(() => {
    if (!journeyApplication) return null;
    return (
      <>
        <Tooltip title={loanStatusPlain(journeyApplication.status)} arrow>
          <Chip
            size="small"
            label={journeyApplication.status.replace(/_/g, ' ')}
            color={statusChipColor}
            variant={statusChipColor === 'success' ? 'filled' : 'outlined'}
            sx={{ fontWeight: 700 }}
          />
        </Tooltip>
        <Chip size="small" variant="outlined" label={journeyApplication.productType.replace(/_/g, ' ')} />
        {deviceRiskQ.data?.deviceRiskReview ? (
          <Chip size="small" color="warning" label="Device risk: review" />
        ) : null}
        {/* A corrected application must never read as untouched. */}
        {journeyApplication.manualEditCount ? (
          <Tooltip title="A reviewer has corrected data on this application" arrow>
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={`Manually edited · ${journeyApplication.manualEditCount}`}
              sx={{ fontWeight: 700 }}
            />
          </Tooltip>
        ) : null}
      </>
    );
  }, [journeyApplication, deviceRiskQ.data?.deviceRiskReview, statusChipColor]);

  const pageChrome = useMemo<AdminPageChromeState>(
    () => ({
      startAdornment: isMdUp ? chromeStart : null,
      breadcrumbs: [{ label: 'Applications', to: applicationsListTo }],
      title: 'Loan application',
      subtitle: journeyApplication?.applicationId ?? (decodedId || undefined),
      endAdornment: chromeEnd,
    }),
    [journeyApplication?.applicationId, chromeStart, chromeEnd, decodedId, isMdUp, applicationsListTo],
  );

  useAdminPageChrome(pageChrome);

  const tabStatuses = useMemo(() => {
    if (!journeyQ.data) {
      return {
        overview: 'neutral' as const,
        device: 'neutral' as const,
        identity: 'pending' as const,
        income: 'pending' as const,
        employer: 'pending' as const,
        'loan-review': 'pending' as const,
        decision: 'pending' as const,
        'manual-review': 'pending' as const,
        'data-notes': 'neutral' as const,
      };
    }
    return {
      ...computeApplicationTabStatuses({
        journey: journeyQ.data,
        deviceRisk: deviceRiskQ.data ?? null,
        employerFootprintStatus: footprintQ.data?.status ?? null,
      }),
      // Never gated: a reviewer must be able to record a correction or a note at
      // any stage, including the terminal ones.
      'data-notes': 'neutral' as const,
    };
  }, [journeyQ.data, deviceRiskQ.data, footprintQ.data?.status]);

  useEffect(() => {
    const fromQuery = TAB_INDEX_FROM_QUERY[tabQuery];
    if (fromQuery === undefined) return;
    // Honor explicit ?tab= deep links (e.g. loan-review “see Employer tab”).
    setTab(fromQuery);
  }, [tabQuery]);

  useEffect(() => {
    // Only auto-correct when the URL did not request a specific tab.
    if (tabQuery) return;
    const key = TAB_QUERY_BY_INDEX[tab];
    if (key && !isApplicationTabEnabled(key, tabStatuses[key])) {
      setTab(firstEnabledTabIndex(tabStatuses));
    }
  }, [tab, tabStatuses, tabQuery]);

  useEffect(() => {
    const status = journeyApplication?.status.trim().toUpperCase();
    if (tab !== TAB_INDEX_FROM_QUERY['manual-review']) return;
    if (status && status !== 'MANUAL_REVIEW') {
      selectTab(0, { replace: true });
    }
  }, [journeyApplication?.status, tab, selectTab]);

  if (!decodedId) {
    return <Typography>Missing application id.</Typography>;
  }

  if (journeyQ.isPending) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Loading application…
        </Typography>
      </Box>
    );
  }

  if (journeyQ.isError || !journeyQ.data) {
    const applicationNotFound = isApplicationNotFoundAxiosError(journeyQ.error);
    return (
      <Stack spacing={2}>
        {!isMdUp ? (
          <Button
            component={RouterLink}
            to={applicationsListTo}
            size="small"
            variant="outlined"
            startIcon={<ArrowBackRoundedIcon />}
            sx={{ alignSelf: 'flex-start' }}
          >
            Applications
          </Button>
        ) : null}
        <Alert
          severity={applicationNotFound ? 'warning' : 'error'}
          action={
            applicationNotFound ? (
              <Button
                color="inherit"
                size="small"
                component={RouterLink}
                to={applicationsListTo}
              >
                Applications
              </Button>
            ) : (
              <Button color="inherit" size="small" onClick={() => journeyQ.refetch()}>
                Retry
              </Button>
            )
          }
        >
          {applicationNotFound ? (
            <>
              <Typography component="span" sx={{ display: 'block', fontWeight: 700 }}>
                {APPLICATION_NOT_FOUND_ADMIN_TITLE}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, display: 'block' }}>
                {APPLICATION_NOT_FOUND_ADMIN_BODY}
              </Typography>
            </>
          ) : (
            messageFromOrchestratorAxiosError(journeyQ.error) || 'Failed to load application.'
          )}
        </Alert>
      </Stack>
    );
  }

  const { application, executions, verification } = journeyQ.data;

  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.28 }}>
      <Box sx={{ width: '100%' }}>
      {!isMdUp ? (
        <Stack spacing={1.25} sx={{ mb: 2 }}>
          <Button
            component={RouterLink}
            to={applicationsListTo}
            size="small"
            variant="outlined"
            startIcon={<ArrowBackRoundedIcon fontSize="small" />}
            sx={{ alignSelf: 'flex-start', borderColor: 'divider' }}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
              Loan application
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
              {application.applicationId}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Tooltip title={loanStatusPlain(application.status)} arrow>
              <Chip
                size="small"
                label={application.status.replace(/_/g, ' ')}
                color={loanStatusChipColor(application.status)}
                variant={loanStatusChipColor(application.status) === 'success' ? 'filled' : 'outlined'}
                sx={{ fontWeight: 700 }}
              />
            </Tooltip>
            <Chip size="small" variant="outlined" label={application.productType.replace(/_/g, ' ')} />
            {deviceRiskQ.data?.deviceRiskReview ? (
              <Chip size="small" color="warning" label="Device risk: review" />
            ) : null}
          </Stack>
        </Stack>
      ) : null}

      <ApplicationDetailTabBar
        value={tab}
        onChange={(index) => selectTab(index, { replace: true })}
        tabStatuses={tabStatuses}
        tabCounts={{ 'data-notes': application.manualEditCount ?? 0 }}
      />

      {/* One edit dialog for the page, above the tabs — so switching tabs cannot
          unmount it and discard a half-typed reason. */}
      <ApplicationEditingProvider
        applicationId={application.applicationId}
        applicationStatus={application.status}
      >
      <AnimatePresence mode="wait">
        <motion.div
          id="application-detail-tab-panel"
          key={tab}
          initial={{ opacity: 0, x: 12 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -12 }}
          transition={{ type: 'spring', stiffness: 320, damping: 30 }}
          style={{
            scrollMarginTop:
              'calc(var(--admin-topbar-height, 52px) + 5.5rem)',
          }}
        >
          {tab === 0 && (
            <ApplicationOverviewTab
              application={application}
              employerFootprintStatus={footprintQ.data?.status ?? null}
              employerFootprintAdminMessage={footprintQ.data?.adminMessage ?? null}
              employerFootprintPaystubsReason={footprintQ.data?.paystubsRequiredReason ?? null}
              employerFootprintActive={isEmployerFootprintActive(footprintQ.data?.status)}
              incomeRecords={verification.incomeRecords}
              employerRecords={verification.employerRecords}
              deviceRisk={deviceRiskQ.data ?? null}
              identitySessions={verification.identitySessions}
              snapshotErrors={verification.snapshotErrors}
              onNavigate={selectTabByKey}
            />
          )}

          {tab === 1 && (
            <ApplicationDeviceIntelligenceTab applicationId={application.applicationId} />
          )}

          {tab === 2 && (
            <ApplicationIdentityTab
              applicationId={application.applicationId}
              snapshotErrors={verification.snapshotErrors}
              application={application}
              onboarding={deviceRiskQ.data?.onboarding ?? null}
              timeline={journeyQ.data.timeline ?? null}
            />
          )}

          {tab === 3 && (
            <ApplicationIncomeTab
              applicationId={application.applicationId}
              data={verification.incomeRecords}
              snapshotErrors={verification.snapshotErrors}
              lastActionResult={application.lastActionResult}
              incomeJump={incomeJump}
            />
          )}

          {tab === 4 && (
            <ApplicationDecisionTab
              applicationId={application.applicationId}
              status={application.status}
              executions={executions}
              loanReview={application.loanReview}
              decisionEngine={application.decisionEngine}
              incomeRecords={verification.incomeRecords}
              applicationStatus={application.status}
              declineReason={application.declineReason}
              application={application}
              deviceRisk={deviceRiskQ.data ?? null}
              onNavigate={selectTabByKey}
            />
          )}

          {tab === 5 && (
            <ApplicationEmployerTab
              applicationId={application.applicationId}
              application={application}
              data={verification.employerRecords}
              snapshotErrors={verification.snapshotErrors}
            />
          )}

          {tab === 6 && (
            <ApplicationLoanReviewTab
              applicationId={application.applicationId}
              loanReview={application.loanReview}
              onNavigateToTab={selectTabByKey}
            />
          )}

          {tab === 7 && (
            <ApplicationEmployeeDecisionPanel
              application={application}
              deviceRisk={deviceRiskQ.data ?? null}
              onNavigate={selectTabByKey}
            />
          )}

          {tab === 8 && (
            <ApplicationDataAndNotesTab
              applicationId={application.applicationId}
              applicationStatus={application.status}
            />
          )}
        </motion.div>
      </AnimatePresence>
      </ApplicationEditingProvider>
      </Box>
    </motion.div>
  );
}
